import React, { useEffect, useState, useCallback } from "react";
import { classNames } from "../utils";
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";

interface ToastProps {
  message: string;
  title?: string;
  actionLabel?: string;
  onAction?: () => void;
  onDismiss: () => void;
  type?:
    | "success"
    | "success-white-bg"
    | "notification"
    | "warning"
    | "error"
    | "info"
    | "neutral"
    | "global-notification";
  autoDismiss?: boolean;
  duration?: number; // in milliseconds
  playSound?: boolean; // Whether to play notification sound (default: false)
}

const Toast = ({
  message,
  title,
  actionLabel,
  onAction,
  onDismiss,
  type = "neutral",
  autoDismiss = true,
  duration = 5000,
  playSound = false,
}: ToastProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss();
    }, 300); // Match the transition duration
  }, [onDismiss]);

  // Auto-dismiss timer
  useEffect(() => {
    if (autoDismiss && isVisible) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [autoDismiss, duration, isVisible, handleDismiss]);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    try {
      // Create a simple beep sound using Web Audio API
      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Configure a pleasant notification sound
      oscillator.frequency.value = 800; // Higher pitch for notification
      oscillator.type = "sine"; // Smooth sine wave

      // Fade in/out for a softer sound
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(
        0.1,
        audioContext.currentTime + 0.01
      );
      gainNode.gain.exponentialRampToValueAtTime(
        0.005,
        audioContext.currentTime + 0.15
      );

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.15);
    } catch (error) {
      // Silently fail if audio context is not available (e.g., autoplay restrictions)
      console.debug("Could not play notification sound:", error);
    }
  }, []);

  // Slide in animation and play sound (if enabled)
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
      if (playSound) {
        playNotificationSound();
      }
    }, 10); // Small delay to ensure DOM is ready

    return () => clearTimeout(timer);
  }, [playNotificationSound, playSound]);

  const handleAction = () => {
    onAction?.();
    handleDismiss();
  };

  const getTypeStyles = () => {
    switch (type) {
      case "error":
        return {
          container: "bg-red-50 border-red-200 shadow-lg",
          text: "text-red-800",
          title: "text-red-900",
          actionButton: "bg-red-600 hover:bg-red-700 text-white",
          dismissButton: "text-red-600 hover:text-red-800 hover:bg-red-100",
        };
      case "warning":
        return {
          container: "bg-yellow-50 border-l-4 border-yellow-500 shadow-lg",
          text: "text-yellow-800",
          title: "text-yellow-900",
          actionButton: "bg-yellow-600 hover:bg-yellow-700 text-white",
          dismissButton:
            "text-yellow-600 hover:text-yellow-800 hover:bg-yellow-100",
        };
      case "success":
        return {
          container: "bg-green-50 border-l-4 border-green-500 shadow-lg",
          text: "text-green-800",
          title: "text-green-900",
          actionButton: "bg-green-600 hover:bg-green-700 text-white",
          dismissButton:
            "text-green-600 hover:text-green-800 hover:bg-green-100",
        };
      case "success-white-bg":
        return {
          container: "bg-white border border-gray-200 shadow-sm",
          containerPadding: "px-4 py-3",
          contentAlign: "items-center",
          text: "text-gray-700",
          title: "text-gray-900",
          actionButton: "bg-green-600 hover:bg-green-700 text-white",
          dismissButton: "text-gray-400 hover:text-gray-600",
        };
      case "notification":
        return {
          container: "bg-white border border-gray-200 shadow-sm",
          containerPadding: "px-4 py-3",
          contentAlign: "items-center",
          text: "text-gray-700",
          title: "text-gray-900",
          actionButton:
            "text-blue-600 hover:text-blue-700 font-medium px-0 py-0",
          dismissButton: "text-gray-400 hover:text-gray-600",
        };
      case "info":
        return {
          container: "bg-blue-50 border-l-4 border-blue-500 shadow-lg",
          text: "text-blue-800",
          title: "text-blue-900",
          actionButton: "bg-blue-600 hover:bg-blue-700 text-white",
          dismissButton: "text-blue-600 hover:text-blue-800 hover:bg-blue-100",
        };
      case "global-notification":
        return {
          container: "bg-darkBlue border-l-4 border-darkBlue shadow-lg",
          text: "text-white",
          title: "text-white font-bold",
          actionButton: "bg-white hover:bg-gray-100 text-darkBlue",
          dismissButton: "text-white hover:text-gray-200 hover:bg-opacity-20",
        };
      case "neutral":
      default:
        return {
          container: "bg-gray-50 border-gray-200",
          text: "text-gray-600",
          title: "text-gray-900",
          actionButton: "text-blue-700 hover:bg-gray-100",
          dismissButton: "text-gray-500 hover:text-gray-800",
        };
    }
  };

  const typeStyles = getTypeStyles();

  return (
    <div
      className={classNames(
        "fixed top-4 left-1/2 transform -translate-x-1/2 z-50",
        "w-full max-w-md mx-4",
        "transition-all duration-300 ease-in-out",
        isVisible && !isExiting
          ? "translate-y-0 opacity-100"
          : "translate-y-[-100%] opacity-0"
      )}
    >
      <div
        data-testid="toast-container"
        className={classNames(
          "rounded-lg",
          typeStyles.containerPadding ?? "",
          "flex gap-3",
          typeStyles.contentAlign ?? "",
          "backdrop-blur-sm",
          typeStyles.container
        )}
      >
        {/* Icon */}
        {type !== "notification" && (
          <div
            className={classNames(
              "flex-shrink-0",
              type !== "success-white-bg" ? "mt-0.5" : ""
            )}
          >
            {type === "error" && (
              <svg
                className="w-5 h-5 text-red-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            {type === "warning" && (
              <svg
                className="w-5 h-5 text-yellow-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            {type === "success" && (
              <svg
                className="w-5 h-5 text-green-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            {type === "success-white-bg" && (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
                <CheckIcon
                  className="h-4 w-4 text-green-600"
                  strokeWidth={2.5}
                />
              </div>
            )}
            {type === "global-notification" && (
              <ExclamationTriangleIcon className="w-5 h-5 text-orange-600" />
            )}
            {(type === "info" || type === "neutral") && (
              <svg
                className="w-5 h-5 text-blue-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {type === "global-notification" && title ? (
            <div
              data-testid="toast-message"
              className={classNames("text-sm leading-relaxed", typeStyles.text)}
            >
              <span className={classNames(typeStyles.title)}>{title}</span>
              {message}
            </div>
          ) : (
            <>
              {title && (
                <div
                  data-testid="toast-title"
                  className={classNames(
                    "text-sm font-semibold mb-1",
                    typeStyles.title
                  )}
                >
                  {title}
                </div>
              )}
              <div
                data-testid="toast-message"
                className={classNames(
                  "text-sm leading-relaxed",
                  typeStyles.text
                )}
              >
                {message}
              </div>
            </>
          )}
        </div>

        {/* Action Button */}
        {actionLabel && (
          <button
            data-testid="toast-action-button"
            onClick={handleAction}
            className={classNames(
              "flex-shrink-0 text-sm",
              type === "notification"
                ? typeStyles.actionButton
                : classNames(
                    "px-3 py-1.5 rounded-md font-medium",
                    "transition-colors duration-200",
                    "focus:outline-none focus:ring-2 focus:ring-offset-2",
                    typeStyles.actionButton
                  )
            )}
          >
            {actionLabel}
          </button>
        )}

        {/* Dismiss Button */}
        <button
          data-testid="toast-dismiss-button"
          onClick={handleDismiss}
          className={classNames(
            "flex-shrink-0 p-1 rounded-md",
            "transition-colors duration-200",
            "focus:outline-none focus:ring-2 focus:ring-offset-2",
            typeStyles.dismissButton
          )}
          aria-label="Dismiss"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default Toast;
